import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { catchError, map, shareReplay } from 'rxjs/operators'
import { IceCorporateAction } from '../models/ice-corporate-action'
import { extractEventSubtype } from '../util/ice-field-extractor'

const CORPORATE_ACTIONS_HOST = 'helios-corporate-actions'

export interface IceConfig {
  maxPastPositionLookbackDays: number
}

export interface BasicClient {
  id: number
  name: string
}

interface IceCorporateActionResponse {
  eventId: string
  type: string | null
  actionType: string | null
  effectiveDate: string | null
  parentSecurityId: number
  rawData: Record<string, unknown>
}

@Injectable({
  providedIn: 'root',
})
export class IceCorporateActionsService {
  constructor(private readonly httpClient: HttpClient) {}

  private config$: Observable<IceConfig> | null = null

  getConfig(): Observable<IceConfig> {
    if (!this.config$) {
      this.config$ = this.httpClient
        .get<IceConfig>(`${CORPORATE_ACTIONS_HOST}/ice/config`)
        .pipe(
          catchError(err => {
            this.config$ = null
            return throwError(err)
          }),
          shareReplay(1),
        )
    }
    return this.config$
  }

  private clients$: Observable<BasicClient[]> | null = null

  getClients(): Observable<BasicClient[]> {
    if (!this.clients$) {
      this.clients$ = this.httpClient
        .get<BasicClient[]>(`${CORPORATE_ACTIONS_HOST}/ice/clients`)
        .pipe(
          catchError(err => {
            this.clients$ = null
            return throwError(err)
          }),
          shareReplay(1),
        )
    }
    return this.clients$
  }

  /**
   * @param clientId comma-separated client ID(s)
   * @param beginDate ISO date string (yyyy-MM-dd) for range start; empty string = no lower bound
   * @param endDate ISO date string (yyyy-MM-dd) for range end; empty string = no upper bound
   */
  getByClient(
    clientId: string,
    beginDate: string,
    endDate: string,
  ): Observable<IceCorporateAction[]> {
    return this.httpClient
      .get<IceCorporateActionResponse[]>(`${CORPORATE_ACTIONS_HOST}/ice`, {
        params: { clientId, beginDate, endDate },
      })
      .pipe(
        map(responses =>
          responses.map(r => this.toIceCorporateAction(r, clientId)),
        ),
      )
  }

  private toIceCorporateAction(
    response: IceCorporateActionResponse,
    clientId: string,
  ): IceCorporateAction {
    return {
      eventId: response.eventId,
      type: response.type ?? '',
      eventType: extractEventSubtype(response.rawData, response.type) ?? '',
      action: response.actionType ?? '',
      actionType: response.actionType,
      effectiveDate: response.effectiveDate ?? '',
      parentSecurity: response.parentSecurityId.toString(),
      parentSecurityId: response.parentSecurityId,
      clientId,
      rawData: response.rawData,
    }
  }
}
